<template>
  <div class="space-y-6">
    <!-- Connection Status -->
    <div class="bg-gray-stack-1 rounded-token-lg shadow-3d border border-hairline p-4 sm:p-6">
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div class="flex items-center space-x-3 sm:space-x-4">
          <img :src="squareLogo" alt="Square Logo" class="h-10 w-10 sm:h-12 sm:w-12 bg-white rounded-token-sm p-1 flex-shrink-0" />
          <div class="min-w-0 flex-1">
            <h3 class="text-base sm:text-lg font-medium text-gray-900 dark:text-gray-50">Square Integration</h3>
            <p class="text-xs sm:text-sm text-gray-600 dark:text-gray-300">Connect your Square account to enable payment processing</p>
          </div>
        </div>
        <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4 sm:flex-shrink-0">
          <span
            :class="[
              authError ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-700' :
              isConnected ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-700' : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-600',
              'px-3 py-1 rounded-token-sm text-xs sm:text-sm font-medium text-center sm:text-left'
            ]"
          >
            {{ authError ? 'Connection Expired' : (isConnected ? 'Connected' : 'Not Connected') }}
          </span>
          <button
            v-if="!isConnected || authError"
            @click="connectSquare"
            class="inline-flex items-center justify-center px-4 py-2 border border-hairline text-sm font-medium rounded-token-sm shadow-3d text-white focus:ring-[color:var(--ring-primary)] ring-2 ring-offset-2 transition-fast w-full sm:w-auto"
            :style="{ backgroundColor: 'var(--theme-primary-500)' }"
          >
            {{ authError ? 'Reconnect Square' : 'Connect Square' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Authentication Error Warning Banner -->
    <div v-if="authError" class="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 dark:border-red-500 p-4 rounded-token-sm">
      <div class="flex">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-red-400 dark:text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3 flex-1">
          <h3 class="text-sm font-medium text-red-800 dark:text-red-200">
            Square Connection Expired
          </h3>
          <div class="mt-2 text-sm text-red-700 dark:text-red-300">
            <p>
              Your Square connection has expired and deposits cannot be sent. Please reconnect your Square account to continue processing deposits.
            </p>
          </div>
          <div class="mt-4">
            <button
              @click="connectSquare"
              class="inline-flex items-center px-3 py-2 border border-red-300 dark:border-red-700 text-sm font-medium rounded-token-sm text-red-700 dark:text-red-200 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 focus:ring-[color:var(--ring-primary)] ring-2 ring-offset-2 transition-fast"
            >
              Reconnect Square Account
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Connected Account Details -->
    <div v-if="isConnected" class="bg-gray-stack-1 rounded-token-lg shadow-3d border border-hairline p-4 sm:p-6">
      <div class="space-y-4">
        <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <div class="min-w-0 flex-1">
            <h4 class="font-medium text-gray-900 dark:text-gray-50">Connected Account</h4>
            <p class="text-sm text-gray-600 dark:text-gray-300 break-words">{{ squareData.merchantId || 'Unknown' }}</p>
          </div>
          <button 
            @click="disconnectSquare" 
            :disabled="isRevoking"
            class="inline-flex items-center justify-center px-3 py-1.5 border border-hairline text-xs font-medium rounded-token-sm text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 focus:ring-[color:var(--ring-primary)] ring-2 ring-offset-2 transition-fast w-full sm:w-auto sm:flex-shrink-0"
          >
            <svg 
              v-if="isRevoking" 
              class="animate-spin -ml-1 mr-2 h-3 w-3 text-red-700 dark:text-red-400" 
              xmlns="http://www.w3.org/2000/svg" 
              fill="none" 
              viewBox="0 0 24 24"
            >
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {{ isRevoking ? 'Disconnecting...' : 'Disconnect' }}
          </button>
        </div>
        
        <!-- Square Locations -->
        <div v-if="squareData.locations && squareData.locations.length > 0" class="pt-3 border-t border-hairline">
          <h4 class="font-medium mb-2 text-gray-900 dark:text-gray-50">Square Locations</h4>
          <p class="text-sm text-gray-600 dark:text-gray-300 mb-2">
            Select the Square location to use with Tint Bolt.
          </p>
          <div class="space-y-2">
            <div v-for="location in squareData.locations" :key="location.id" 
                 class="border rounded-token-sm p-3 hover:bg-gray-stack-2 dark:hover:bg-gray-stack-3 cursor-pointer transition-colors duration-200"
                 :class="{'bg-gray-stack-2 dark:bg-gray-stack-3': squareData.selectedLocationId === location.id, 'border-hairline': squareData.selectedLocationId !== location.id}"
                 @click="selectLocation(location.id)">
              <div class="flex justify-between items-start sm:items-center gap-3">
                <div class="min-w-0 flex-1">
                  <h5 class="font-medium text-sm text-gray-900 dark:text-gray-50 break-words">{{ location.name }}</h5>
                  <p class="text-xs text-gray-600 dark:text-gray-300 break-words">{{ location.address ? formatAddress(location.address) : 'No address' }}</p>
                </div>
                <div v-if="squareData.selectedLocationId === location.id" class="text-[var(--theme-primary-600)] dark:text-[var(--theme-primary-400)] flex-shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 pt-3 border-t border-hairline">
          <button
            @click="fetchMerchantLocations" 
            :disabled="isRefreshing"
            class="inline-flex items-center justify-center px-4 py-2 border border-hairline text-sm font-medium rounded-token-sm shadow-3d text-gray-700 dark:text-gray-300 bg-gray-stack-1 dark:bg-gray-stack-2 hover:bg-gray-stack-2 dark:hover:bg-gray-stack-3 focus:ring-[color:var(--ring-primary)] ring-2 ring-offset-2 transition-fast w-full sm:w-auto"
          >
            <svg 
              v-if="isRefreshing"
              class="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-700 dark:text-gray-300" 
              xmlns="http://www.w3.org/2000/svg" 
              fill="none" 
              viewBox="0 0 24 24"
            >
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {{ isRefreshing ? 'Fetching...' : 'Fetch Locations' }}
          </button>
        </div>
      </div>
    </div>

    <ShadowSquarePermissions :connected="isConnected" @reconnect="connectSquare" />
    <ShadowPaymentFoundation />

    <!-- Payment Preferences -->
    <div v-if="isConnected && !isS3Shadow" class="bg-gray-stack-1 rounded-token-lg shadow-3d border border-hairline p-4 sm:p-6">
      <h3 class="text-base sm:text-lg font-medium text-gray-900 dark:text-gray-50 mb-4">Payment Preferences</h3>
      <div v-if="!squareData.selectedLocationId" class="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 dark:border-yellow-500 p-4 mb-4 rounded-token-sm">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-yellow-400 dark:text-yellow-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
          </div>
          <div class="ml-3">
            <p class="text-sm text-yellow-700 dark:text-yellow-300">
              Please select a primary Square location above to enable payment preferences.
            </p>
          </div>
        </div>
      </div>
      <div class="space-y-4" :class="{ 'opacity-50 pointer-events-none': !squareData.selectedLocationId }">

        <!-- Deposit Collection -->
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div class="flex-1 min-w-0">
            <h4 class="text-sm font-medium text-gray-900 dark:text-gray-50">Deposit Collection</h4>
            <p class="text-sm text-gray-600 dark:text-gray-300">Allow collecting deposits for bookings</p>
          </div>
          <Switch
            v-model="preferences.square_deposit"
            :disabled="!squareData.selectedLocationId"
            :class="[
              preferences.square_deposit ? 'bg-theme-primary-500' : 'bg-gray-200 dark:bg-gray-600',
              'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[color:var(--ring-primary)] focus:ring-offset-2'
            ]"
          >
            <span
              :class="[
                preferences.square_deposit ? 'translate-x-5' : 'translate-x-0',
                'pointer-events-none relative inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
              ]"
            />
          </Switch>
        </div>

        <!-- Deposit Settings -->
        <div v-if="preferences.square_deposit" class="mt-4 bg-gray-stack-2 dark:bg-gray-stack-3 rounded-token-lg p-4 border border-gray-stack-3 dark:border-white/10">
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div class="flex-1 min-w-0">
              <h4 class="text-sm font-medium text-gray-900 dark:text-gray-50">Send Deposit by Default</h4>
              <p class="text-sm text-gray-600 dark:text-gray-300">Automatically request deposit for new bookings</p>
            </div>
            <Switch
              v-model="preferences.square_deposit_default"
              :class="[
                preferences.square_deposit_default ? 'bg-theme-primary-500' : 'bg-gray-200 dark:bg-gray-600',
                'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[color:var(--ring-primary)] focus:ring-offset-2'
              ]"
            >
              <span
                :class="[
                  preferences.square_deposit_default ? 'translate-x-5' : 'translate-x-0',
                  'pointer-events-none relative inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                ]"
              />
            </Switch>
          </div>

          <div v-if="preferences.square_deposit_default" class="mt-4 space-y-3">
            <div class="flex items-center space-x-4">
              <label class="flex items-center">
                <input
                  type="radio"
                  v-model="preferences.square_deposit_type"
                  value="percentage"
                  class="h-4 w-4 text-theme-primary-600 focus:ring-[color:var(--ring-primary)] border-gray-300 dark:border-gray-600"
                />
                <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">Percentage</span>
              </label>
              <label class="flex items-center">
                <input
                  type="radio"
                  v-model="preferences.square_deposit_type"
                  value="fixed"
                  class="h-4 w-4 text-theme-primary-600 focus:ring-[color:var(--ring-primary)] border-gray-300 dark:border-gray-600"
                />
                <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">Fixed Amount</span>
              </label>
            </div>

            <div class="flex items-center space-x-2">
              <div class="relative rounded-token-sm shadow-sm">
                <input
                  type="number"
                  v-model="preferences.square_deposit_amount"
                  :min="0"
                  :max="preferences.square_deposit_type === 'percentage' ? 100 : undefined"
                  step="0.01"
                  class="block w-24 rounded-token-sm border-hairline pr-12 focus:border-theme-primary-500 focus:ring-[color:var(--ring-primary)] sm:text-sm bg-white dark:bg-gray-stack-2 text-gray-900 dark:text-gray-50"
                  placeholder="0"
                />
                <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                  <span class="text-gray-500 dark:text-gray-400 sm:text-sm">
                    {{ preferences.square_deposit_type === 'percentage' ? '%' : '$' }}
                  </span>
                </div>
              </div>
              <span class="text-sm text-gray-600 dark:text-gray-300">deposit</span>
            </div>
          </div>

          <!-- Add this new switch after the deposit amount input -->
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4 pt-4 border-t border-gray-stack-3 dark:border-white/10">
            <div class="flex-1 min-w-0">
              <h4 class="text-sm font-medium text-gray-900 dark:text-gray-50">Show Deposit Disclaimer</h4>
              <p class="text-sm text-gray-600 dark:text-gray-300">Display deposit information on the landing page</p>
            </div>
            <Switch
              v-model="preferences.square_deposit_disclaimer"
              :class="[
                preferences.square_deposit_disclaimer ? 'bg-theme-primary-500' : 'bg-gray-200 dark:bg-gray-600',
                'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[color:var(--ring-primary)] focus:ring-offset-2'
              ]"
            >
              <span
                :class="[
                  preferences.square_deposit_disclaimer ? 'translate-x-5' : 'translate-x-0',
                  'pointer-events-none relative inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                ]"
              />
            </Switch>
          </div>
        </div>

        <DepositAutomationSettings v-if="preferences.square_deposit" v-model="preferences.depositAutomation" />
        <p v-if="preferenceSaveError" role="alert" class="mt-2 text-sm text-red-600">{{ preferenceSaveError }} <button type="button" class="underline" @click="savePreferences">Retry saving</button></p>

        <!-- Send Invoice -->
        <!-- <div class="flex items-center justify-between">
          <div>
            <h4 class="text-sm font-medium text-gray-900 dark:text-gray-50">Send Invoice</h4>
            <p class="text-sm text-gray-600 dark:text-gray-300">Allow customers to pay via email invoice</p>
            <span class="inline-flex items-center px-2 py-0.5 rounded-token-sm text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 border border-yellow-200 dark:border-yellow-700 mt-1">
              Coming Soon
            </span>
          </div>
          <Switch
            v-model="preferences.square_invoice"
            :disabled="true"
            :class="[
              'bg-gray-200 dark:bg-gray-600',
              'relative inline-flex h-6 w-11 flex-shrink-0 cursor-not-allowed rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[color:var(--ring-primary)] focus:ring-offset-2'
            ]"
          >
            <span
              class="translate-x-0 pointer-events-none relative inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
            />
          </Switch>
        </div> -->

        <!-- Payment Link -->
        <!-- <div class="flex items-center justify-between">
          <div>
            <h4 class="text-sm font-medium text-gray-900 dark:text-gray-50">Payment Link</h4>
            <p class="text-sm text-gray-600 dark:text-gray-300">Generate shareable payment links for customers</p>
            <span class="inline-flex items-center px-2 py-0.5 rounded-token-sm text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 border border-yellow-200 dark:border-yellow-700 mt-1">
              Coming Soon
            </span>
          </div>
          <Switch
            v-model="preferences.square_payment_link"
            :disabled="true"
            :class="[
              'bg-gray-200 dark:bg-gray-600',
              'relative inline-flex h-6 w-11 flex-shrink-0 cursor-not-allowed rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[color:var(--ring-primary)] focus:ring-offset-2'
            ]"
          >
            <span
              class="translate-x-0 pointer-events-none relative inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
            />
          </Switch>
        </div> -->
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { projectFirestore } from '../../../firebase/config';
import { refreshSquareToken } from '../../../firebase/functions';
import store from '../../../store';
import { doc, getDoc, setDoc, updateDoc, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Switch } from '@headlessui/vue';
import DepositAutomationSettings from './DepositAutomationSettings.vue';
import ShadowPaymentFoundation from './ShadowPaymentFoundation.vue';
import ShadowSquarePermissions from './ShadowSquarePermissions.vue';
const isShadowAccount = computed(() => store.state.userConfig?.project === 'shadow');
const isS3Shadow = computed(() => store.state.userConfig?.project === 'shadow' && store.state.userConfig?.profile === 's3Tint-invXT2bn');

// Hardcoded client IDs for development and testing
// In production, these should be set in your .env file
// IMPORTANT: Replace these with your actual Square application IDs
const SANDBOX_CLIENT_ID = 'sandbox-sq0idb-SDcCU1la9Yaj40_GFyHSlw'; // Your actual sandbox client ID
const PRODUCTION_CLIENT_ID = 'sq0idp-RVA7lCkcZhoit3FsHoEQbg'; // Your actual production client ID

// Square logo
const squareLogo = `data:image/svg+xml;base64,${btoa('<svg xmlns="http://www.w3.org/2000/svg" width="396" height="396" fill="transparent"><rect x="36" y="36" width="324" height="324" rx="30" ry="30" stroke="#373F4A" stroke-width="72"/><rect x="65" y="65" width="266" height="266" rx="28" ry="28" stroke="#373F4A" stroke-width="14"/><rect x="144" y="144" width="108" height="108" rx="12" ry="12" fill="#373F4A"/></svg>')}`;

const isConnected = ref(false);
const isRefreshing = ref(false);
const isRevoking = ref(false);
const authError = ref(false);
const squareData = ref({
  accessToken: '',
  refreshToken: '',
  expiresAt: null,
  createdAt: null,
  refreshAt: null,
  merchantId: '',
  locations: [],
  selectedLocationId: null
});
const origin = ref('');
const preferences = ref({
  square_invoice: false,
  square_payment_link: false,
  square_terminal: false,
  square_deposit: false,
  square_deposit_default: false,
  square_deposit_type: 'percentage',
  square_deposit_amount: 25,
  square_deposit_disclaimer: true,
  depositAutomation: { enabled: false, reminderHours: 2, cancellationHours: 2 }
});


const preferenceSaveError = ref('');
let lastSavedPreferences = '';
let preferencesLoaded = false;
const hydratePreferences = userData => {
  const saved = userData.square?.preferences || {};
  const next = {
    ...saved,
    square_invoice: saved.square_invoice || false,
    square_payment_link: saved.square_payment_link || false,
    square_terminal: saved.square_terminal || false,
    square_deposit: saved.square_deposit || false,
    square_deposit_default: saved.square_deposit_default || false,
    square_deposit_type: saved.square_deposit_type || 'percentage',
    square_deposit_amount: saved.square_deposit_amount || 25,
    square_deposit_disclaimer: saved.square_deposit_disclaimer ?? true,
    depositAutomation: saved.depositAutomation || { enabled: false, reminderHours: 2, cancellationHours: 2 },
  };
  lastSavedPreferences = JSON.stringify(next);
  preferences.value = next;
  preferencesLoaded = true;
};

// Get the redirect URI (still needed for OAuth flow)
const redirectUri = computed(() => {
  if (!origin.value) return '';
  
  // For localhost, include the port number if present
  if (origin.value.includes('localhost')) {
    // Use the full origin including port (e.g., http://localhost:8080)
    return `${origin.value}/square-callback`;
  }
  
  return `${origin.value}/square-callback`;
});

// Initialize window-dependent values
onMounted(() => {
  if (typeof window !== 'undefined') {
    origin.value = window.location.origin;
  }
});

// Load preferences from Firestore
const loadPreferences = async () => {
  try {
    const userRef = doc(projectFirestore, 'users', store.state.currentUser.uid);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      hydratePreferences(userData);
    }
  } catch (error) {
    console.error('Error loading preferences:', error);
  }
};

// Save preferences to Firestore
const savePreferences = async () => {
  try {
    const userRef = doc(projectFirestore, 'users', store.state.currentUser.uid);
    
    const next = JSON.parse(JSON.stringify(preferences.value));
    await updateDoc(userRef, { 'square.preferences': next });
    lastSavedPreferences = JSON.stringify(next);
    preferenceSaveError.value = '';

    // Update store's userConfig.square preferences
    if (!store.state.userConfig.square) {
      store.state.userConfig.square = {};
    }
    store.state.userConfig.square.preferences = preferences.value;
  } catch (error) {
    console.error('Error saving preferences:', error);
    preferenceSaveError.value = 'Your payment settings could not be saved. Please try again.';
  }
};

// Watch for preference changes
watch(preferences, () => {
  if (!preferencesLoaded || JSON.stringify(preferences.value) === lastSavedPreferences) return;
  savePreferences();
}, { deep: true });


// Connect to Square
const connectSquare = async () => {
  // Generate a more secure random string for CSRF protection
  // This produces a random string that should work better with the CSRF library
  const generateSecureState = () => {
    const array = new Uint8Array(24);
    window.crypto.getRandomValues(array);
    return Array.from(array, byte => ('0' + (byte & 0xFF).toString(16)).slice(-2)).join('');
  };
  
  const state = generateSecureState();
  
  // Store the state in localStorage to verify when the user returns
  localStorage.setItem('squareOAuthState', state);
  
  // Determine environment
  const useSandbox = isShadowAccount.value && isConnected.value
    ? squareData.value.isSandbox === true || ['localhost', 'sandbox'].includes(store.state.userConfig?.environment) || store.state.currentUser.uid === 'jqopyo9iyKYHhb3qF9RCPMDbg6B2'
    : origin.value.includes('localhost');
  const environment = useSandbox ? 'localhost' : 'production';
  
  // For Sandbox: open Square Sandbox Dashboard in background (don't block the flow)
  if (environment === 'localhost') {
    // Open sandbox dashboard in background - user can log in there if needed
    // But don't block the OAuth flow - continue immediately
    window.open('https://developer.squareupsandbox.com/apps', '_blank');
  }
  
  // Verify user is logged in
  if (!store.state.currentUser || !store.state.currentUser.uid) {
    console.error('User is not logged in');
    alert('You must be logged in to connect to Square');
    return;
  }
  
  // Save the state value directly (not as a CSRF token) to the user's document
  try {
    const userDocRef = doc(projectFirestore, 'users', store.state.currentUser.uid);
    
    // First, check if the user document exists
    const docSnap = await getDoc(userDocRef);
    if (!docSnap.exists()) {
      console.error('User document does not exist');
      alert('Error: Your user profile is not set up correctly. Please contact support.');
      return;
    }
    
    // Save the raw state value (not a CSRF token) with merge to preserve other fields
    // Also save the redirect URI so the backend can use it
    await setDoc(userDocRef, { 
      csrf: state, // We're saving the raw state value (for simple string comparison later)
      squareEnvironment: environment,
      squareRedirectUri: redirectUri.value // Save redirect URI for backend
    }, { merge: true });
    
    console.log('Saved OAuth state and redirect URI:', {
      state: state.substring(0, 10) + '...',
      environment,
      redirectUri: redirectUri.value
    });
    
    
    // Verify the value was saved correctly
    const verifyDoc = await getDoc(userDocRef);
    if (verifyDoc.exists() && verifyDoc.data().csrf === state) {
    } else {
      console.error('State value verification failed');
      alert('Error: Could not save security state. Please try again or contact support.');
      return;
    }
  } catch (error) {
    console.error('Error saving state value:', error);
    alert('Error: Could not save security state. Please try again or contact support.');
    return;
  }
  
  // Get client ID - in Vue, environment variables must be prefixed with VUE_APP_ and accessed directly
  let clientId;
  
  // Ensure client ID doesn't have duplicate sandbox- prefix
  if (environment === 'localhost') {
    // Strip sandbox- prefix if it exists to prevent duplication
    clientId = SANDBOX_CLIENT_ID.startsWith('sandbox-') ? SANDBOX_CLIENT_ID : `sandbox-${SANDBOX_CLIENT_ID}`;
  } else {
    clientId = PRODUCTION_CLIENT_ID;
  }
  
  // Construct the redirect URI
  const encodedRedirectUri = encodeURIComponent(redirectUri.value);
  
  // Define scopes with proper encoding
  const scopes = [
    'MERCHANT_PROFILE_READ',
    'PAYMENTS_READ',
    'PAYMENTS_WRITE',
    'ORDERS_READ',
    'ORDERS_WRITE',
    'CUSTOMERS_READ',
    'CUSTOMERS_WRITE',
    'INVENTORY_READ',
    'INVENTORY_WRITE'
  ].join('+');
  
  // Construct the OAuth URL with additional debugging info
  let baseUrl;
  if (environment === 'localhost') {
    baseUrl = 'https://connect.squareupsandbox.com/oauth2/authorize';
  } else {
    baseUrl = 'https://connect.squareup.com/oauth2/authorize';
  }
  
  // Construct the full URL with query parameters
  const oauthUrl = `${baseUrl}?client_id=${clientId}&scope=${scopes}&response_type=code&state=${state}&redirect_uri=${encodedRedirectUri}${environment === 'localhost' ? '&session=false' : ''}`;

  console.log('Opening Square OAuth URL:', {
    environment,
    redirectUri: redirectUri.value,
    clientId: clientId.substring(0, 10) + '...',
    oauthUrl: oauthUrl.substring(0, 100) + '...'
  });
  
  // Always try opening in a new window for Sandbox
  const shouldOpenNewWindow = environment === 'localhost';
  
  if (shouldOpenNewWindow) {
    // Open in new window for sandbox
    const oauthWindow = window.open(oauthUrl, '_blank');
    if (!oauthWindow) {
      alert('Popup blocked! Please allow popups for this site and try again.');
      return;
    }
  } else {
    // Redirect to Square's OAuth page
    window.location.href = oauthUrl;
  }
};

// Disconnect from Square
const disconnectSquare = async () => {
  try {
    isRevoking.value = true;
    // Get Firebase functions instance
    const functions = getFunctions();
    
    // Create callable function reference for squareRevoke
    const squareRevokeFn = httpsCallable(functions, 'squareRevoke');
    
    // Call the squareRevoke cloud function
    const result = await squareRevokeFn({
      environment: origin.value.includes('localhost') ? 'localhost' : 'production'
    });
    
    if (result.data.status) {
      // Clear local state
      isConnected.value = false;
      squareData.value = {
        accessToken: '',
        refreshToken: '',
        expiresAt: null,
        createdAt: null,
        refreshAt: null,
        merchantId: '',
        locations: [],
        selectedLocationId: null
      };
      
      // Clear deposit settings in companyProfile
      try {
        const companyProfileRef = doc(
          projectFirestore,
          "companyProfile",
          store.state.companyProfileName
        );
        
        await updateDoc(companyProfileRef, {
          "config.depositSettings": {
            enabled: false,
            isDefault: false,
            type: 'percentage',
            amount: 0
          }
        });
        
        // Also reset local preferences
        preferences.value.square_deposit = false;
        preferences.value.square_deposit_default = false;
      } catch (profileError) {
        console.error('Error clearing deposit settings:', profileError);
      }
    } else {
      throw new Error(result.data.message || 'Failed to revoke Square access');
    }
  } catch (error) {
    console.error('Error disconnecting Square:', error);
    alert('Failed to disconnect from Square. Please try again or contact support.');
  } finally {
    isRevoking.value = false;
  }
};

// Refresh Square token
const refreshToken = async () => {
  if (isRefreshing.value) return;
  
  isRefreshing.value = true;
  try {
    // Call Firebase function to refresh token
    const result = await refreshSquareToken({
      userId: store.state.currentUser.uid
    });
    
    if (result.data.status === 'Success') {
      // Refresh the data
      await fetchSquareData();
      
      // After refreshing token, fetch locations
      await fetchMerchantLocations();
    }
  } catch (error) {
    console.error('Error refreshing Square token:', error);
  } finally {
    isRefreshing.value = false;
  }
};

// Fetch Square integration data
const fetchSquareData = async () => {
  try {
    const userDocRef = doc(projectFirestore, 'users', store.state.currentUser.uid);
    const docSnap = await getDoc(userDocRef);

    if (docSnap.exists()) {
      const userData = docSnap.data();
      
      if (userData.square && userData.tokenSecure) {
        squareData.value = userData.square;
        squareData.value.merchantId = userData.merchant || 'Unknown';
        // Ensure locations is set (or default to empty array)
        squareData.value.locations = userData.square.locations || [];
        // Get selected location
        squareData.value.selectedLocationId = userData.square.selectedLocationId || null;
        // Check for authentication error
        authError.value = userData.square.authError === true;
        isConnected.value = true;
      } else {
        isConnected.value = false;
        authError.value = false;
      }
    }
  } catch (error) {
    console.error('Error fetching Square integration data:', error);
  }
};

// Fetch Square merchant locations
const fetchMerchantLocations = async () => {
  if (!isConnected.value) return;
  
  try {
    isRefreshing.value = true;
    
    // Get Firebase functions instance
    const functions = getFunctions();
    
    // Create callable function reference
    const fetchLocationsFn = httpsCallable(functions, 'fetchLocations');
    
    // Call the fetchLocations cloud function
    const result = await fetchLocationsFn({
      userId: store.state.currentUser.uid,
    });
    
    if (result.data.status === 'Success') {
      await fetchSquareData(); // Refresh data to get updated locations
    } else {
      console.error('Error fetching locations:', result.data.message);
    }
  } catch (error) {
    console.error('Error calling fetchLocations function:', error);
  } finally {
    isRefreshing.value = false;
  }
};

// Select a Square location
const selectLocation = async (locationId) => {
  try {
    // Update the local state
    squareData.value.selectedLocationId = locationId;
    
    // Update in Firestore
    const userDocRef = doc(projectFirestore, 'users', store.state.currentUser.uid);
    await updateDoc(userDocRef, {
      'square.selectedLocationId': locationId
    });
    
  } catch (error) {
    console.error('Error selecting Square location:', error);
  }
};

// Format address for display
const formatAddress = (address) => {
  const parts = [];
  if (address.address_line_1) parts.push(address.address_line_1);
  if (address.locality) parts.push(address.locality);
  if (address.administrative_district_level_1) parts.push(address.administrative_district_level_1);
  return parts.join(', ');
};

// Set up a real-time listener for Square connection status and preferences
onMounted(async () => {
  await fetchSquareData();
  await loadPreferences(); // Load preferences on mount
  
  // Set up a Firestore listener to update connection status and preferences in real-time
  if (store.state.currentUser && store.state.currentUser.uid) {
    const userDocRef = doc(projectFirestore, 'users', store.state.currentUser.uid);
    const unsubscribe = onSnapshot(userDocRef, (docSnap) => {
      if (docSnap.exists()) {
        const userData = docSnap.data();
        
        if (userData.square && userData.tokenSecure) {
          squareData.value = userData.square;
          squareData.value.merchantId = userData.merchant || 'Unknown';
          // Ensure locations is set (or default to empty array)
          squareData.value.locations = userData.square.locations || [];
          // Get selected location
          squareData.value.selectedLocationId = userData.square.selectedLocationId || null;
          // Check for authentication error
          authError.value = userData.square.authError === true;
          isConnected.value = true;
          
          // Update preferences from Firestore
          hydratePreferences(userData);
          
          // If we just connected and don't have locations yet, fetch them
          if (isConnected.value && (!squareData.value.locations || squareData.value.locations.length === 0)) {
            fetchMerchantLocations();
          }
        } else {
          // Square connection was lost or revoked
          const wasConnected = isConnected.value;
          isConnected.value = false;
          authError.value = false;
          
          // If we just disconnected, clear deposit settings in companyProfile
          if (wasConnected) {
            try {
              const companyProfileRef = doc(
                projectFirestore,
                "companyProfile",
                store.state.companyProfileName
              );
              
              updateDoc(companyProfileRef, {
                "config.depositSettings": {
                  enabled: false,
                  isDefault: false,
                  type: 'percentage',
                  amount: 0
                }
              });
              
              // Reset local preferences
              preferences.value.square_deposit = false;
              preferences.value.square_deposit_default = false;
            } catch (profileError) {
              console.error('Error clearing deposit settings:', profileError);
            }
          }
        }
      } else {
        isConnected.value = false;
      }
    }, (error) => {
      console.error('Error listening to Square connection status:', error);
    });
    
    // Clean up the listener when component is unmounted
    return () => unsubscribe();
  }
});

// Watch Square deposit preferences and update the companyProfile
watch(
  [
    () => preferences.value.square_deposit,
    () => preferences.value.square_deposit_default,
    () => preferences.value.square_deposit_type,
    () => preferences.value.square_deposit_amount,
    () => preferences.value.square_deposit_disclaimer
  ],
  async () => {
    // If no location is selected, reset deposit preferences
    if (!squareData.value.selectedLocationId) {
      preferences.value.square_deposit = false;
      preferences.value.square_deposit_default = false;
      return;
    }

    try {
      // Update the company profile with the deposit settings
      const companyProfileRef = doc(
        projectFirestore,
        "companyProfile",
        store.state.companyProfileName
      );

      // First, get the current company profile to preserve other settings
      const companyProfileSnap = await getDoc(companyProfileRef);
      if (companyProfileSnap.exists()) {
        // If deposit is disabled, make sure all related settings are properly cleared
        if (!preferences.value.square_deposit) {
          await updateDoc(companyProfileRef, {
            "config.depositSettings": {
              enabled: false,
              isDefault: false,
              type: preferences.value.square_deposit_type,
              amount: preferences.value.square_deposit_amount,
              showDisclaimer: preferences.value.square_deposit_disclaimer
            }
          });
        } else {
          // Normal update with current preferences
          await updateDoc(companyProfileRef, {
            "config.depositSettings": {
              enabled: preferences.value.square_deposit,
              isDefault: preferences.value.square_deposit_default,
              type: preferences.value.square_deposit_type,
              amount: preferences.value.square_deposit_amount,
              showDisclaimer: preferences.value.square_deposit_disclaimer
            }
          });
        }
      }
    } catch (error) {
      console.error("Error updating company profile with deposit settings:", error);
    }
  },
  { deep: true }
);

// Make sure this is exposed to parent components through defineExpose
defineExpose({
  isConnected,
  squareData,
  refreshData: fetchSquareData  // Expose a method to refresh the data
});
</script>
